import {useEffect, useState} from "react";
import {Outlet, useLocation, useParams, useSearchParams} from "react-router-dom";
import {SerializedError} from "@reduxjs/toolkit";
import {FetchBaseQueryError, skipToken} from "@reduxjs/toolkit/dist/query";
import {FormattedMessage} from "react-intl";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {useFilters} from "../../hooks/useFilters";
import {selectItemsLite, setDesignItemsLite, setIsLastPage} from "../../services/designItemsSlice";
import {useFetchDesignItemsLiteAnonymousQuery} from "../../services/clApiSlice";
import {useAddNewLogMutation} from "../../services/seqApiSlice";
import {selectSeqData} from "../../services/seqDataSlice";
import {initialCDIFilterState} from "../../services/defaultCDIFilter";
import {
	setLastDesignItemConnectorFilter,
	setLastDesignItemFeatureFilter,
	setLastDesignItemSearchFilter,
} from "../../services/lastDesignItemSearchFilter";
import {
	getDesignItemSearchFilterFromLocationStateOrLocalStorage,
	getLastConnectorFilterIfRedirectedFromLogin,
	getLastFeatureFilterIfRedirectedFromLogin,
	getLastPopularityFilterIfRedirectedFromLogin,
	getLastSortPreferenceIfRedirectedFromLogin,
} from "../../functions/filterDesginItems/filterDesignItems";
import setNewLog from "../../functions/setNewLog/setNewLog";
import Filters from "../layout/Filters";
import ConnectionSummary from "../layout/ConnectionSummary";
import Loader from "../common/Loader";
import SigninModal from "../common/SigninModal";
import ExpiredTokenModal from "../common/ExpiredTokenModal";
import ConDesignItem from "../common/ConDesignItem";
import NoResults from "../common/NoResults";
import CDIsContainer from "../common/CDIsContainer";
import ShowMoreButton from "../common/ShowMoreButton";
import SortButtons from "../common/SortButtons";
import {UserIds} from "../../types/User";
import {
	CDIFilterState,
	CDIFilterStateWithFeatureFilters,
	MemberModel,
	SearchQueryModel,
} from "../../types/CDIFilter";
import {ConDesignItemLite} from "../../types/ConDesignItem";

interface CDIsPageProps {
	userIds: UserIds | undefined;
	isAuthenticated: boolean | undefined;
	isLoadingUserInformations: boolean;
	isFetchingUserInformations: boolean;
	userInformationsError: FetchBaseQueryError | SerializedError | undefined;
	expiredTokenItem: ConDesignItemLite | null;
	setExpiredTokenModal: (item: ConDesignItemLite | null) => void;
}

const CDIsPage = ({
	userIds,
	isAuthenticated,
	expiredTokenItem,
	setExpiredTokenModal,
}: CDIsPageProps) => {
	
	const [isLoadingShowMore, setIsLoadingShowMore] = useState<boolean>(false);
	const [paginationFrom, setPaginationFrom] = useState<number>(0);
	const [members, setMembers] = useState<MemberModel[]>(
		initialCDIFilterState.members,
	);
	const [searchQuery, setSearchQuery] = useState<SearchQueryModel>();
	const [selectedItem, setSelectedItem] = useState<ConDesignItemLite | null>(null);
	const [showSigninModal, setShowSigninModal] = useState<boolean>(false);
	const dispatch = useAppDispatch();
	const [addNewLog] = useAddNewLogMutation();
	const {itemId} = useParams();
	const [searchParams] = useSearchParams();
	
	const location = useLocation();
	const seqData = useAppSelector(selectSeqData);
	const designItemsLite = useAppSelector(selectItemsLite);
	const cdiFilterState = location.state as
		| CDIFilterStateWithFeatureFilters
		| CDIFilterState;
	
	// Use the filters hook
	const resetDesignItems = () => {
		setPaginationFrom(0);
		dispatch(
			setDesignItemsLite({
				data: {
					designItems: [],
					pageSize: 0,
					isLastPage: true,
					totalItems: "filtering",
				},
				isShowMoreClicked: false,
			}),
		);
	};
	
	const filters = useFilters({
		onFilterChange: resetDesignItems,
	});
	
	const {
		connectorFilters,
		featureFilters,
		popularityFilters,
		designCodeFilter,
		sortBy,
		setConnectorFilters,
		setFeatureFilters,
		setPopularityFilters,
		setDesignCodeFilter,
		setSortBy,
		handleApplyFilters,
		handleApplyPopularityFilter,
		handleApplySortPreference,
		handleSetOnlyFilter,
		clearAllFilters,
	} = filters;
	
	// Load filters from URL params if redirected from login
	useEffect(() => {
		const lastFeatureFilter = getLastFeatureFilterIfRedirectedFromLogin(searchParams);
		const lastConnectorFilter = getLastConnectorFilterIfRedirectedFromLogin(searchParams);
		const lastPopularityFilter = getLastPopularityFilterIfRedirectedFromLogin(searchParams);
		const lastSortPreference = getLastSortPreferenceIfRedirectedFromLogin(searchParams);
		
		if (lastFeatureFilter !== null) {
			setFeatureFilters([...lastFeatureFilter.featureFilter]);
		}
		
		if (lastConnectorFilter !== null) {
			setConnectorFilters([...lastConnectorFilter.connectorFilter]);
		}
		
		if (lastPopularityFilter !== null) {
			setPopularityFilters({...lastPopularityFilter.popularityFilter});
		}
		
		if (lastSortPreference !== null) {
			setSortBy(lastSortPreference);
		}
		// eslint-disable-next-line
	}, [searchParams]);
	
	// Load feature filters from location state
	useEffect(() => {
		if (
			cdiFilterState &&
			"featureFilters" in cdiFilterState &&
			cdiFilterState.featureFilters
		) {
			setFeatureFilters(cdiFilterState.featureFilters);
		}
	}, [cdiFilterState, setFeatureFilters]);
	
	// Load members and search query from location state or localStorage
	useEffect(() => {
		const {
			members: retrievedMembers,
		} = getDesignItemSearchFilterFromLocationStateOrLocalStorage(cdiFilterState);
		
		const queryParams = new URLSearchParams(location.search);
		
		// If it's redirect from Viewer, parse the query string and set
		if (queryParams.get("filterFromViewer")) {
			const filterFromViewer = JSON.parse(queryParams.get("filterFromViewer")!);
			const viewerMembers: MemberModel[] = filterFromViewer.members.map(
				(member: any, index: number) => ({
					id: index + 1,
					memberFilter: member,
				}),
			);
			
			setMembers(viewerMembers);
			setLastDesignItemSearchFilter({
				members: [...viewerMembers],
				searchQuery: undefined,
				selectedMember: viewerMembers[0],
			});
			
			// Parse feature filter to match CL structure
			const updatedConnectorFilters = connectorFilters.map(f => {
				const found = filterFromViewer.featureFilters.find(
					(filter: any) => filter.feature === f.feature,
				);
				
				if (found) {
					return found.clause ? {...f, clause: 1} : {...f, clause: 0};
				}
				return {...f, clause: 0};
			});
			
			const updatedFeatureFilters = featureFilters.map(f => {
				const found = filterFromViewer.featureFilters.find(
					(filter: any) =>
						filter.feature === f.feature &&
						!['bolt', 'weld'].includes(filter.feature)
				);
				
				if (found) {
					return found.clause ? {...f, clause: 1} : {...f, clause: 0};
				}
				return {...f, clause: 0};
			});
			
			setConnectorFilters(updatedConnectorFilters);
			setLastDesignItemConnectorFilter({connectorFilter: updatedConnectorFilters});
			setFeatureFilters(updatedFeatureFilters);
			setLastDesignItemFeatureFilter({featureFilter: updatedFeatureFilters});
			
			// Remove query string from page to not interfere with the app
			window.history.replaceState(null, "", window.location.pathname);
		} else if (retrievedMembers.length > 0 && (JSON.stringify(members) !== JSON.stringify(retrievedMembers))) {
			setMembers(retrievedMembers);
		}
		// eslint-disable-next-line
	}, [location, cdiFilterState, setConnectorFilters, setFeatureFilters]);
	
	// Logging effect
	useEffect(() => {
		const timestamp = new Date();
		if (
			seqData &&
			userIds &&
			userIds.licenseId &&
			userIds.userId &&
			userIds.correlationId &&
			designItemsLite.designItems?.length
		) {
			const logObject = {
				logString: setNewLog(
					timestamp,
					"{Count} design items returned for the user with ID {UserAccountId} and public design set",
					userIds.correlationId,
					userIds.licenseId,
					userIds.userId,
					designItemsLite.designItems.length,
				),
				seqEndpoint: seqData.seqEndpoint,
				seqApiKey: seqData.seqKey,
			};
			addNewLog(logObject);
		}
	}, [addNewLog, userIds, seqData, designItemsLite]);
	
	const {
		data: designItemsLiteAnonymousData,
		error: designItemsLiteAnonymousError,
		isFetching: isFetchingDesignItemsAnonymousLite,
	} = useFetchDesignItemsLiteAnonymousQuery(
		searchQuery?.searchQueryString
			? {...searchQuery, paginationFrom: paginationFrom}
			: skipToken,
	);
	
	useEffect(() => {
		if (designItemsLiteAnonymousData?.designItems) {
			dispatch(
				setDesignItemsLite({
					data: designItemsLiteAnonymousData,
					isShowMoreClicked: paginationFrom !== 0,
				}),
			);
			dispatch(setIsLastPage(designItemsLiteAnonymousData.isLastPage));
		}
	}, [dispatch, designItemsLiteAnonymousData, paginationFrom]);
	
	// Build search query string whenever filters change
	useEffect(() => {
		if (members) {
			const searchQueryString = encodeURIComponent(
				JSON.stringify({
					members: members.map((member) => member.memberFilter),
					featureFilters: [...connectorFilters, ...featureFilters],
					popularityFilters: popularityFilters,
					designCodeFilter,
					sortBy,
				}),
			);
			
			setSearchQuery({searchQueryString});
		}
	}, [featureFilters, connectorFilters, popularityFilters, members, designCodeFilter, sortBy]);
	
	useEffect(() => {
		document.body.style.overflow = showSigninModal ? "hidden" : "auto";
	}, [showSigninModal]);
	
	useEffect(() => {
		if (!isFetchingDesignItemsAnonymousLite)
			setIsLoadingShowMore(isFetchingDesignItemsAnonymousLite);
	}, [isFetchingDesignItemsAnonymousLite]);
	
	const handleRefetchData = () => {
		if (isAuthenticated) {
			setPaginationFrom(paginationFrom + designItemsLite.pageSize);
			setIsLoadingShowMore(true);
		} else {
			setShowSigninModal(true);
		}
	};
	
	const handleSelectedItemSet = (item: ConDesignItemLite) => {
		setSelectedItem(item);
		if (!isAuthenticated) {
			setShowSigninModal(true);
		}
	};
	
	const handleCloseModal = () => {
		setShowSigninModal(false);
		setSelectedItem(null);
	};
	
	const handleCloseExpiredTokenModal = () => {
		setExpiredTokenModal(null);
	};
	
	return (
		<div className="CDIs-page-layout">
			<ConnectionSummary members={members}/>
			
			<div className="CDIs-page-sublayout">
				<Filters
					featureFilters={featureFilters}
					connectorFilters={connectorFilters}
					popularityFilters={popularityFilters}
					designCodeFilter={designCodeFilter}
					setFeatureFilters={setFeatureFilters}
					setConnectorFilters={setConnectorFilters}
					setPopularityFilters={setPopularityFilters}
					setDesignCodeFilter={setDesignCodeFilter}
					handleApplyFilters={handleApplyFilters}
					handleApplyPopularityFilter={handleApplyPopularityFilter}
					handleSetOnlyFilter={handleSetOnlyFilter}
					clearAllFilters={clearAllFilters}
					resetDesignItems={resetDesignItems}
					showPopularityFilters={true}
					showDesignCodeFilter={true}
					onShowSigninModal={() => setShowSigninModal(true)}
				/>
				
				{showSigninModal && (
					<SigninModal
						item={selectedItem}
						handleCloseModal={handleCloseModal}
					/>
				)}
				{expiredTokenItem && (
					<ExpiredTokenModal
						item={expiredTokenItem}
						handleCloseModal={handleCloseExpiredTokenModal}
					/>
				)}
				
				<div className="CDIs-page-content">
					<div className="CDIs-page-content-bar">
						{designItemsLite.totalItems ? (
							<div className="total-items">
								{designItemsLite.totalItems}{" "}
								<FormattedMessage id="Results" defaultMessage="results"/>
							</div>
						) : (
							""
						)}
						
						<SortButtons sortBy={sortBy} onSortChange={handleApplySortPreference}/>
					</div>
					
					<div className="con-design-items-page">
						{isFetchingDesignItemsAnonymousLite && !isLoadingShowMore ? (
							itemId ? (
								<div className="loader-container loader-container-detail">
									<Loader/>
								</div>
							) : (
								<div className="loader-container">
									<Loader/>
								</div>
							)
						) : (
							<>
								{designItemsLite.totalItems ? (
									<CDIsContainer itemId={itemId}>
										{designItemsLite.designItems?.map((item, index) => (
											<ConDesignItem
												conDesignItem={item}
												key={index}
												setSelectedItem={handleSelectedItemSet}
												isAuthenticated={isAuthenticated as boolean}
												redirectUri={`/set/public/item/${item.id}`}
												source="design"
											/>
										))}
									</CDIsContainer>
								) : (
									""
								)}
								
								{designItemsLite.isLastPage !== null &&
									designItemsLite.totalItems !== 0 &&
									designItemsLite.totalItems !== "filtering" &&
									!designItemsLite.isLastPage && (
										<ShowMoreButton
											itemId={itemId}
											onRefetchData={handleRefetchData}
											isLoading={
												isLoadingShowMore &&
												isFetchingDesignItemsAnonymousLite
											}
										/>
									)}
							</>
						)}
						
						{(designItemsLite.totalItems === 0 && !isFetchingDesignItemsAnonymousLite) ||
						(designItemsLiteAnonymousError && !isFetchingDesignItemsAnonymousLite) ? (
							<NoResults/>
						) : (
							""
						)}
					</div>
				</div>
				<Outlet/>
			</div>
		</div>
	);
};

export default CDIsPage;